// Collapsible card with a heading + count badge, used by AdminTab.
// Each section starts collapsed so the page doesn't dump every payload
// at once; click the header to expand. Body is rendered as either
// formatted JSON (default) or whatever children the caller passes.
import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

interface Props {
  title: string;
  badge?: string | number | null;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function AdminSection({ title, badge, defaultOpen = false, children }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bsp-card" style={{ padding: 0, marginBottom: 12, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "10px 14px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
        }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <strong style={{ flex: 1 }}>{title}</strong>
        {badge != null && (
          <span style={{
            fontSize: 11,
            background: "var(--bsp-blue-pale)",
            color: "var(--bsp-blue)",
            padding: "2px 8px",
            borderRadius: 999,
          }}>
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: "8px 14px 14px", borderTop: "1px solid var(--bsp-border)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Tiny pretty-print helper. JSON.stringify with 2-space indent, capped
// length so a 50K diResult doesn't lock the browser.
export function JsonBlock({ value, maxChars = 40_000 }: { value: unknown; maxChars?: number }) {
  if (value === undefined || value === null) {
    return <em style={{ color: "var(--bsp-fg-muted)" }}>(empty)</em>;
  }
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  const truncated = text.length > maxChars;
  const displayed = truncated ? text.slice(0, maxChars) + `\n…(${text.length - maxChars} more chars)` : text;
  return (
    <pre style={{
      margin: 0,
      padding: 10,
      background: "var(--ink-100, #F5F7FA)",
      border: "1px solid var(--bsp-border)",
      borderRadius: 4,
      fontSize: 11,
      lineHeight: 1.45,
      overflow: "auto",
      maxHeight: 480,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}>
      {displayed}
    </pre>
  );
}
